/**
 * Hormiga Dormida — Botanical Edition
 * Main JavaScript Module
 */

import { createLogger } from './logger.js';
import { createTraceContext, trackProductEvent } from './logger.js';
import { initializeErrorTracking, setErrorTrackingUser } from './error-tracking.js';
import { initializeMetricsCollection, incrementMetric, recordDuration } from './performance-metrics.js';
import { hasPrivacyConsent, initializePrivacyCompliance } from './privacy-compliance.js';
import { requireLogin, clearSession, getCurrentUser } from './login-gate.js';
import { openProfile } from './profile.js';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase.js';

const logger = createLogger('web:main');

document.addEventListener('DOMContentLoaded', () => {
  const logoutBtn = document.getElementById('nav-logout-btn');
  const profileBtn = document.getElementById('nav-profile-btn');

  if (logoutBtn) {
    logoutBtn.addEventListener('click', () => {
      clearSession().then(() => window.location.reload());
    });
  }

  // Boot the public page immediately — no auth required
  bootApp();

  // Show/hide auth-dependent nav items based on session state
  onAuthStateChanged(auth, (user) => {
    if (user) {
      if (logoutBtn) logoutBtn.classList.remove('hidden');
      if (profileBtn) {
        profileBtn.classList.remove('hidden');
        profileBtn.onclick = () => openProfile(user);
      }
    } else {
      if (logoutBtn) logoutBtn.classList.add('hidden');
      if (profileBtn) profileBtn.classList.add('hidden');
    }
  });

  // "Join the Colony" CTA triggers login gate when clicked
  const joinColonyBtn = document.querySelector('a[href="#signal"].btn-primary, a.btn-primary[href="#signal"]');
  if (joinColonyBtn) {
    joinColonyBtn.addEventListener('click', (e) => {
      if (!getCurrentUser()) {
        e.preventDefault();
        requireLogin().then(() => {
          window.location.hash = '#signal';
        });
      }
    });
  }
});

function bootApp() {
  const consentState = initializePrivacyCompliance();
  initializeMetricsCollection();
  if (hasPrivacyConsent('errorTracking')) {
    initializeErrorTracking();
  }

  const startupTrace = createTraceContext('page_initialized');
  incrementMetric('web.page.dom_content_loaded', {
    consent_decided: Boolean(consentState.decidedAt)
  });
  recordDuration('web.page.load_ms', performance.now());

  logger.info('Initializing landing page interactions');
  trackProductEvent('landing_page_initialized', { surface: 'web' }, startupTrace);
  initHeaderScroll();
  initMobileNav();
  initSmoothScroll();
  initScrollReveal();
  initNumberCounters();
  initFormHandler();
  initCardHoverGlow();
}

/* ── Sticky header background on scroll ── */
function initHeaderScroll() {
  const header = document.querySelector('.site-header');
  if (!header) {
    logger.warn('Header element not found for scroll behavior');
    return;
  }
  const headerEl = /** @type {HTMLElement} */ (header);

  let ticking = false;

  function onScroll() {
    if (!ticking) {
      requestAnimationFrame(() => {
        headerEl.classList.toggle('scrolled', window.scrollY > 40);
        ticking = false;
      });
      ticking = true;
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* ── Mobile navigation ── */
function initMobileNav() {
  const toggle = document.querySelector('.nav-toggle');
  const mobileNav = document.querySelector('.mobile-nav');
  if (!toggle || !mobileNav) {
    logger.warn('Mobile navigation elements are missing', {
      hasToggle: Boolean(toggle),
      hasMobileNav: Boolean(mobileNav)
    });
    return;
  }

  const links = mobileNav.querySelectorAll('.mobile-nav-link');

  toggle.addEventListener('click', () => {
    const navTrace = createTraceContext('mobile_nav_toggled');
    const isOpen = mobileNav.classList.toggle('open');
    toggle.classList.toggle('active');
    mobileNav.setAttribute('aria-hidden', String(!isOpen));
    document.body.style.overflow = isOpen ? 'hidden' : '';
    trackProductEvent('mobile_nav_toggled', { is_open: isOpen }, navTrace);
    logger.info('Mobile navigation toggled', { isOpen }, navTrace);
  });

  links.forEach((link) => {
    link.addEventListener('click', () => {
      mobileNav.classList.remove('open');
      toggle.classList.remove('active');
      mobileNav.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    });
  });
}

/* ── Smooth scroll for anchor links ── */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      const target = document.querySelector(href);
      if (target) {
        e.preventDefault();
        const header = document.querySelector('.site-header');
        if (!header) {
          logger.warn('Cannot smooth-scroll without header element', { href });
          return;
        }

        const headerH = /** @type {HTMLElement} */ (header).offsetHeight;
        const top = target.getBoundingClientRect().top + window.scrollY - headerH - 20;
        window.scrollTo({ top, behavior: 'smooth' });
        trackProductEvent('anchor_navigation', { href }, createTraceContext('anchor_navigation'));
      }
    });
  });
}

/* ── Scroll-triggered reveal animations ── */
function initScrollReveal() {
  // Tag elements that should reveal
  const selectors = [
    '.section-tag',
    '.manifesto-heading',
    '.manifesto-text',
    '.principles-title',
    '.colony-title',
    '.colony-card',
    '.signal-content',
    '.number-item'
  ];

  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((el) => el.classList.add('reveal'));
  });

  // Stagger grids
  document.querySelectorAll('.principles-grid').forEach((el) => {
    el.classList.add('reveal-stagger');
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
  );

  document.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
    observer.observe(el);
  });
}

/* ── Animated number counters ── */
function initNumberCounters() {
  const items = document.querySelectorAll('.number-value[data-target]');

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          animateValue(entry.target);
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.5 }
  );

  items.forEach((el) => observer.observe(el));
}

function animateValue(el) {
  /** @type {HTMLElement & { dataset: DOMStringMap }} */
  const targetEl = /** @type {HTMLElement & { dataset: DOMStringMap }} */ (el);
  const target = parseInt(targetEl.dataset.target || '0', 10);
  const duration = 2000;
  const start = performance.now();

  /** @param {number} now */
  function tick(now) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 4); // ease-out quart
    const current = Math.floor(target * eased);

    if (target >= 1000) {
      targetEl.textContent = (current / 1000).toFixed(current >= target ? 0 : 1) + 'K';
    } else {
      targetEl.textContent = String(current);
    }

    if (progress < 1) {
      requestAnimationFrame(tick);
    } else {
      if (target >= 1000) {
        targetEl.textContent = Math.floor(target / 1000) + 'K';
      } else {
        targetEl.textContent = String(target);
      }
    }
  }

  requestAnimationFrame(tick);
}

/* ── Form handler ── */
function initFormHandler() {
  const form = document.getElementById('signup-form');
  if (!form) return;

  form.addEventListener('submit', async (e) => {
    const submitTrace = createTraceContext('signup_submitted');
    e.preventDefault();

    const input = form.querySelector('input[type="email"]');
    const btn = form.querySelector('button');
    if (!input || !btn) {
      logger.warn('Signup form controls are missing', {
        hasInput: Boolean(input),
        hasButton: Boolean(btn)
      });
      return;
    }

    const email = /** @type {HTMLInputElement} */ (input).value;
    if (!email) return;

    setErrorTrackingUser({
      id: toTelemetryUserId(email),
      username: 'landing-signup'
    });

    trackProductEvent('signup_submitted', { has_email: true }, submitTrace);

    const originalHTML = btn.innerHTML;
    btn.innerHTML = '<span>Planting...</span>';
    btn.disabled = true;
    btn.style.opacity = '0.7';

    await new Promise((r) => setTimeout(r, 1400));

    btn.innerHTML = `
      <span>Seed Planted!</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    `;
    btn.style.background = 'var(--indigo)';
    btn.style.opacity = '1';

    setTimeout(() => {
      btn.innerHTML = originalHTML;
      btn.style.background = '';
      btn.disabled = false;
      /** @type {HTMLInputElement} */ (input).value = '';
    }, 2500);

    logger.info('Signup interaction completed', { email }, submitTrace);
    trackProductEvent('signup_completed', { email_domain: email.split('@')[1] || 'unknown' }, submitTrace);
  });
}

function toTelemetryUserId(email) {
  /** @type {string} */
  const normalizedEmail = String(email || '').trim().toLowerCase();
  let hash = 5381;
  for (let i = 0; i < normalizedEmail.length; i += 1) {
    hash = (hash * 33) ^ normalizedEmail.charCodeAt(i);
  }

  return `lead_${Math.abs(hash)}`;
}

/* ── Card hover glow ── */
function initCardHoverGlow() {
  document.querySelectorAll('.principle-card').forEach((card) => {
    const cardEl = /** @type {HTMLElement} */ (card);
    cardEl.addEventListener(
      'mousemove',
      /** @param {MouseEvent} e */ (e) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        cardEl.style.setProperty('--glow-x', `${x}px`);
        cardEl.style.setProperty('--glow-y', `${y}px`);
        cardEl.style.background = `
        radial-gradient(
          400px circle at var(--glow-x) var(--glow-y),
          rgba(128, 128, 255, 0.06),
          transparent 50%
        ),
        var(--bg)
      `;
      }
    );

    cardEl.addEventListener('mouseleave', () => {
      cardEl.style.background = '';
    });
  });
}
